import type { SelectProps } from '@chakra-ui/react'
import { Select, Spinner } from '@chakra-ui/react'
import React from 'react'
import { withFormControl, type FormFieldProps } from './hoc/withFormControl'

// Component-specific props
export interface SelectFormControlComponentProps {
  placeholder?: string
  variant?: SelectProps['variant']
  valueAsNumber?: boolean
  options?: { 
    value: string | number
    label: string
    group?: string
  }[]
  unselectedText?: string
  isLoading?: boolean
  size?: 'sm' | 'md' | 'lg'
  sx?: SelectProps['sx']
  showGroupNames?: boolean
  w?: SelectProps['w']
}

// Base component that handles select logic
export const SelectFormControlBase = React.forwardRef<
  HTMLSelectElement,
  SelectFormControlComponentProps & FormFieldProps<string | number | null, HTMLSelectElement>
>((props, ref) => {
  const {
    // Form field props
    value,
    onChange,
    onBlur,
    isDisabled,
    name,
    // Component-specific props
    options,
    valueAsNumber,
    unselectedText,
    isLoading,
    placeholder,
    size,
    variant,
    sx,
    showGroupNames,
    w
  } = props

  return (
    <Select
      ref={ref}
      name={name}
      icon={isLoading ? <Spinner /> : undefined}
      value={value ?? ''}
      onChange={(e) => {
        const newValue = e.target.value
        onChange(newValue === '' ? null : valueAsNumber ? parseInt(newValue) : newValue)
        onBlur()
      }}
      isDisabled={isDisabled}
      placeholder={placeholder}
      h={size === 'sm' ? 8 : undefined}
      size={size}
      variant={variant}
      sx={sx}
      w={w}
    >
      {unselectedText && <option value=''>{unselectedText}</option>}
      
      {(() => {
        if (!options) return null
        
        // Separate grouped and ungrouped options
        const ungroupedOptions = options.filter(option => !option.group)
        const groupedOptions = options.filter(option => option.group)
        
        // Group options by group name
        const groups = groupedOptions.reduce((acc, option) => {
          const groupName = option.group!
          if (!acc[groupName]) {
            acc[groupName] = []
          }
          acc[groupName].push(option)
          return acc
        }, {} as Record<string, typeof options>)
        
        return (
          <>
            {/* Render ungrouped options first */}
            {ungroupedOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
            
            {/* Render grouped options */}
            {Object.entries(groups).map(([groupName, groupOptions], index) => (
              <React.Fragment key={groupName}>
                {!showGroupNames && (ungroupedOptions.length > 0 || index > 0) && (
                  <option value="" disabled />
                )}
                {showGroupNames ? (
                  <optgroup label={groupName}>
                    {groupOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ) : (
                  groupOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                )}
              </React.Fragment>
            ))}
          </>
        )
      })()}
    </Select>
  )
})

;(SelectFormControlBase as any).displayName = 'SelectFormControlBase'

// Export the wrapped component
export const SelectFormControl = withFormControl<SelectFormControlComponentProps, string | number | null, HTMLSelectElement>(SelectFormControlBase)

// Export the props type for backward compatibility
export type SelectFormControlProps = SelectFormControlComponentProps